import { useState, type ReactNode } from "react";
import { Button, IconButton, Menu, MenuItem, Stack, Tooltip, Typography } from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

/**
 * A row's controls, in the table's actions cell (the row rule, 2026-10-14). One control stays inline, the one a
 * person reaches for most on that row (Edit on a row they own, Adopt on a library row); everything else sits
 * behind one button that opens a menu named for the row, so four controls never fight for a cell. A menu item that
 * cannot be used now says why in its own words rather than vanishing: a person can see what the row could do and
 * what stands in the way. At a narrow width the table scrolls inside its box and the cell stays one swipe away.
 */
export type RowAction = { label: string; onClick: () => void; disabled?: boolean; /** Why it cannot be used now; shown as the item's secondary line. */ reason?: string; destructive?: boolean };

export function RowActions({ name, primary, more = [], busy = false }: { name: string; primary?: ReactNode; more?: (RowAction | null | false | undefined)[]; busy?: boolean }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const items = more.filter((a): a is RowAction => Boolean(a));
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end" sx={{ whiteSpace: "nowrap" }}>
      {primary}
      {items.length > 0 && (
        <>
          <Tooltip title={`More for ${name}`}>
            <IconButton size="small" aria-label={`More for ${name}`} aria-haspopup="menu" aria-expanded={anchor ? true : undefined} disabled={busy} onClick={(e) => setAnchor(e.currentTarget)}><MoreHorizIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} MenuListProps={{ "aria-label": `Actions for ${name}`, dense: true }}>
            {items.map((a) => (
              <MenuItem key={a.label} disabled={a.disabled} onClick={() => { setAnchor(null); a.onClick(); }} sx={a.destructive ? { color: "error.main" } : undefined}>
                <Stack spacing={0}>
                  <Typography variant="body2">{a.label}</Typography>
                  {a.disabled && a.reason && <Typography variant="caption" color="text.secondary">{a.reason}</Typography>}
                </Stack>
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Stack>
  );
}

/** The inline control, sized for a cell. */
export const CellButton = ({ children, ...props }: React.ComponentProps<typeof Button>) => <Button size="small" variant="text" sx={{ minWidth: 0, px: 1 }} {...props}>{children}</Button>;
